<template>
  <div class="blog-explore-container latest-updates-grid">
    <div class="blog-explore-header">
      <h1>Latest Updates</h1>
      <p class="subtitle">Explore latest updates in our Blog</p>
    </div>

    <div v-if="isSortedByCategory" class="explore-grid">
      <div v-for="(category, index) in pageData.categories"
           :key="category.slug"
           v-show="index < visibleCount && categoryArticleCounts[category.slug] > 0"
           class="explore-card">
        <!-- Category content remains the same -->
      </div>

      <div v-if="visibleCount < pageData.categories.length"
           class="load-more s-flex"
           ref="loadMoreTrigger">
        <s-button class="s-button-link-hover button-primary-dark k s-margin-auto" @click="loadMore">
          Show More Updates
        </s-button>
      </div>
    </div>

    <!-- Updated Article Grid with Pagination -->
    <div v-else class="article-grid">
      <div
        v-for="article in displayedArticles"
        :key="article.id"
        class="article-card"
      >
        <a :href="`/pulse/articles/${article.id}-${article.slug}`" class="article-image-link">
          <img
            :src="article.imageSizes['thumbnail']?.url ?? article.image"
            :alt="article.title"
            class="article-image"
            @error="handleImageError"
          >
        </a>

        <div class="article-content">
          <div class="article-meta">
            <a
              v-if="article.category"
              :href="`/pulse/categories/${article.categorySlug}`"
              class="s-badge s-badge-dark category-link"
            >
              {{ article.category }}
            </a>
            <span class="date">{{ formatDate(article.date) }}</span>
          </div>

          <h2 class="article-title">
            <a :href="`/pulse/articles/${article.id}-${article.slug}`">
              {{ article.title }}
            </a>
          </h2>

          <p class="article-description">{{ article.preview }}</p>
        </div>
      </div>
    </div>
    <!-- Load More Trigger -->
    <div v-if="hasMorePosts" ref="loadMoreTrigger" class="read-more">
      <s-button
        class="button-primary-dark s-button-hover-rise s-margin-y-24"
        @click="() => loadMore(false)"
        :loading="loading"
      >
        {{ loading ? 'Loading...' : 'Show More' }}
      </s-button>
    </div>

    <!-- Subscribe Section -->
    <section class="blog-subscribe">
      <BlogSubscribeForm />
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useData } from 'vike-vue/useData';
import SButton from '#root/components/SButton.vue';
import { getPaginatedArticles } from '#root/services/contentService';
import BlogSubscribeForm from "#root/components/BlogSubscribeForm.vue";
import SIcon from "#root/components/SIcon.vue";

const pageData = useData();
const visibleCount = ref(6);
const loadMoreTrigger = ref(null);
const isSortedByCategory = ref(false);

// Pagination state
const articles = ref(pageData.articles);
const currentPage = ref(1);
const hasNextPage = ref(true);
const loading = ref(false);
const autoLoadCount = ref(0);
const itemsPerPage = 6;

// Computed properties
const displayedArticles = computed(() => articles.value);
const hasMorePosts = computed(() => hasNextPage.value);

const categoryArticleCounts = computed(() => {
  return pageData.categories.reduce((acc, category) => {
    acc[category.slug] = pageData.articles.filter(article => article.categorySlug === category.slug).length;
    return acc;
  }, {});
});

const categoryArticlesMap = computed(() => {
  return pageData.categories.reduce((acc, category) => {
    acc[category.slug] = pageData.articles
      .filter(article => article.categorySlug === category.slug)
      .slice(0, 3);
    return acc;
  }, {});
});

const loadMore = async (isAutoLoad = false) => {
  if (loading.value || !hasNextPage.value) return;

  // If it's an auto-load and we've reached the limit, don't proceed
  if (isAutoLoad && autoLoadCount.value >= 2) return;

  loading.value = true;
  try {
    const nextPage = currentPage.value + 1;
    const result = await getPaginatedArticles(nextPage, itemsPerPage);

    articles.value = [...articles.value, ...result.articles];
    hasNextPage.value = result.hasNextPage;
    currentPage.value = nextPage;

    if (isAutoLoad) {
      autoLoadCount.value++;
    }
  } catch (err) {
    console.error('Error loading more articles:', err);
  } finally {
    loading.value = false;
  }
};

// Intersection Observer for infinite scroll
const observeLoadMore = () => {
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && hasMorePosts.value && !loading.value) {
        loadMore(true);
      }
    },
    { threshold: 0.1 }
  );

  if (loadMoreTrigger.value) {
    observer.observe(loadMoreTrigger.value);
  }

  return observer;
};

let observer;

onMounted(() => {
  observer = observeLoadMore();
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const handleImageError = (event) => {
  event.target.src = '/placeholder-image.jpg';
};
</script>
